import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Colors } from '@/constants/colors';
import { MessageReactionSummary } from '@/services/chat/chatService';

interface ReactionBarProps {
  summary?: MessageReactionSummary;
  onToggle: (emoji: string) => void;
  showPicker?: boolean;
  onClosePicker?: () => void;
}

const DEFAULT_EMOJIS = ['👍', '🔥', '😂', '🎯', '💡'];

export const ReactionBar: React.FC<ReactionBarProps> = ({
  summary,
  onToggle,
  showPicker = false,
  onClosePicker,
}) => {
  const counts = summary?.counts || {};
  const mine = new Set(summary?.mine || []);
  const hasReactions = Object.keys(counts).length > 0;

  const sorted = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  // Chips: show only if we have reactions
  const Chips = hasReactions ? (
    <View style={styles.container}>
      {sorted.map(([emoji, count]) => (
        <TouchableOpacity
          key={emoji}
          style={[styles.chip, mine.has(emoji) && styles.chipMine]}
          onPress={() => onToggle(emoji)}
          activeOpacity={0.8}
        >
          <Text style={styles.chipText}>
            {emoji} {count}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  ) : null;

  // Picker: compact grid of default emojis
  const Picker = showPicker ? (
    <View style={styles.picker}>
      {DEFAULT_EMOJIS.map(emoji => (
        <TouchableOpacity
          key={emoji}
          style={styles.pickerItem}
          onPress={() => {
            onToggle(emoji);
            onClosePicker && onClosePicker();
          }}
          activeOpacity={0.85}
        >
          <Text style={styles.pickerText}>{emoji}</Text>
        </TouchableOpacity>
      ))}
    </View>
  ) : null;

  if (!hasReactions && !showPicker) return null;
  return (
    <View>
      {Chips}
      {Picker}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 6,
  },
  chip: {
    backgroundColor: Colors.background.tertiary,
    borderWidth: 1,
    borderColor: Colors.border.default,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  chipMine: {
    backgroundColor: `${Colors.accent.primary}20`,
    borderColor: Colors.accent.primary,
  },
  chipText: {
    fontSize: 12,
    color: Colors.text.secondary,
    fontWeight: '600',
  },
  picker: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Colors.background.tertiary,
    borderColor: Colors.border.default,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 6,
    paddingVertical: 4,
    marginTop: 4,
  },
  pickerItem: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  pickerText: {
    fontSize: 14,
  },
  chipAdd: {
    backgroundColor: Colors.background.tertiary,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    borderRadius: 12,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  chipAddText: {
    fontSize: 12,
    color: Colors.text.tertiary,
  },
});
